import React, { useState, useEffect, useRef } from 'react';
import { Save, Mail, MessageSquare, Server, Inbox, AlertCircle, CheckCircle, Brain, RefreshCw, CreditCard, TestTube, Settings as SettingsIcon, Bug, Loader2, Zap, ExternalLink, Copy, Link, ChevronDown } from 'lucide-react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { settingsAPI, api } from '../services/api';
import toast from '../utils/toast';
import { toastSetupError } from '../components/SetupErrorMessage';
import { useOnboarding } from '../contexts/OnboardingContext';
import ResendWebhookCard from '../components/ResendWebhookCard';
import BugReportForm from '../components/BugReportForm';
import TestPage from '../components/TestPage';
import { useCredits } from '../contexts/CreditsContext';
import usePlans from '../hooks/usePlans';
import { AI_MODELS, OTHER_MODEL, modelSelectValue } from '../utils/aiModels';
import useConfirm from '../hooks/useConfirm';
import TopUpPacks from '../components/TopUpPacks';
import ResearchProfilesCard from '../components/ResearchProfilesCard';
import './SettingsPage.css';

// Derive public backend URL from REACT_APP_API_BASE_URL (strip trailing /api)
const BACKEND_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');

const WebhookUrlField = ({ label, url, note }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="webhook-field">
      <label className="webhook-field-label">
        <Link size={14} />
        {label}
      </label>
      <div className="setting-inline">
        <input
          type="text"
          readOnly
          value={url}
          className="webhook-url-input"
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          onClick={handleCopy}
          title="Copy URL"
          className={`copy-button ${copied ? 'is-copied' : ''}`}
        >
          {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {note && <small className="setting-note">{note}</small>}
    </div>
  );
};

const WebhookSection = ({ children, title = 'Webhook Setup' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`webhook-collapse ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="webhook-collapse-toggle"
        aria-expanded={open}
      >
        <span className="webhook-collapse-title">
          <Link size={14} />
          {title}
        </span>
        <ChevronDown size={14} className="webhook-collapse-chevron" />
      </button>
      {open && (
        <div className="webhook-collapse-body">
          {children}
        </div>
      )}
    </div>
  );
};

// Small read-only helpers for the redesigned layout (display only, no logic)
const SettingsSection = ({ id, title, description, children }) => (
  <section id={id} className="settings-block">
    <div className="settings-block-head">
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
    <div className="settings-block-body">{children}</div>
  </section>
);

const CardHead = ({ icon: Icon, title, description, configured }) => (
  <div className="settings-card-head">
    <span className="settings-card-icon"><Icon size={17} /></span>
    <div className="settings-card-titles">
      <h3>{title}</h3>
      {description && <p>{description}</p>}
    </div>
    {configured && <span className="settings-pill-success"><CheckCircle size={12} />Configured</span>}
  </div>
);

const CANT_READ_HINT = "Saved key can't be read, please enter it again";

// Shown under a field whose saved value can no longer be decrypted (backend flag <KEY>_unreadable)
const UnreadableHint = ({ show }) => (show ? <small className="setting-error">{CANT_READ_HINT}</small> : null);

// Saving secrets is refused with 503 while the server has no encryption key
const saveErrorText = (error, fallback) => {
  if (error?.response?.status === 503) {
    return error.response?.data?.error || 'Saving keys is temporarily unavailable. Please try again later.';
  }
  return fallback;
};

const SettingRow = ({ label, htmlFor, help, children }) => (
  <div className="setting-row">
    <div className="setting-row-label">
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {help && <small className="setting-help">{help}</small>}
    </div>
    <div className="setting-row-control">{children}</div>
  </div>
);

// Empty is allowed (means "use my account email"); otherwise a simple address, no spaces, max 255
const isValidReplyEmail = (value) => {
  const v = (value || '').trim();
  if (v === '') return true;
  return v.length <= 255 && /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(v);
};

const SENDER_NAME_HELP = 'Sender name is the name recipients see next to your address in their inbox, for example "Alex at Acme <hello@yourdomain.com>". If left empty we use "Outreach Engine".';

// Fields each card's own Save button sends (only the ones that changed)
const CARD_KEYS = {
  resend: ['RESEND_API_KEY', 'SENDER_EMAIL', 'SENDER_NAME'],
  sendgrid: ['SENDGRID_API_KEY'],
  smtp: ['SMTP_SERVER', 'SMTP_PORT', 'SMTP_USERNAME', 'SMTP_PASSWORD'],
  replies: ['REPLY_MODE', 'FORWARD_REPLIES_ENABLED', 'FORWARD_REPLIES_TO'],
  twilio: ['TWILIO_SID', 'TWILIO_AUTH', 'TWILIO_NUMBER'],
  ai: ['OPENROUTER_API_KEY', 'AI_MODEL'],
};

// Unset values the UI already treats as these defaults (so picking the default is not a change)
const KEY_DEFAULTS = { REPLY_MODE: 'app', FORWARD_REPLIES_ENABLED: 'true' };
const normValue = (key, v) => (v === undefined || v === null || v === '' ? (KEY_DEFAULTS[key] || '') : String(v));

// Card ids a /settings#<id> link (setup checklist, "Open Settings" errors) scrolls to
const SETTINGS_ANCHORS = ['resend', 'sender', 'replies', 'delivery-tracking', 'sendgrid', 'smtp', 'twilio', 'ai'];

const SettingsPage = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState({
    SENDGRID_API_KEY: '',
    RESEND_API_KEY: '',
    TWILIO_SID: '',
    TWILIO_AUTH: '',
    TWILIO_NUMBER: '',
    SMTP_SERVER: '',
    SMTP_PORT: '',
    SMTP_USERNAME: '',
    SMTP_PASSWORD: '',
    SENDER_EMAIL: '',
    SENDER_NAME: '',
    OPENROUTER_API_KEY: '',
    AI_MODEL: ''
  });
  // Last loaded/saved values, used to tell which card has unsaved changes
  const [savedSettings, setSavedSettings] = useState({});
  const [cardSaving, setCardSaving] = useState(null);
  const [cardSaved, setCardSaved] = useState(null);
  const [cardErrors, setCardErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [aiModelOther, setAiModelOther] = useState(false);
  const [testingResend, setTestingResend] = useState(false);
  const [testingSendGrid, setTestingSendGrid] = useState(false);
  // SendGrid UI is shown only if a SendGrid key was already saved when settings loaded
  const [sendGridConfigured, setSendGridConfigured] = useState(false);
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [testingTwilio, setTestingTwilio] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [resendDomains, setResendDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [domainConfigs, setDomainConfigs] = useState({});
  const [senderDomain, setSenderDomain] = useState('');
  const [senderLocal, setSenderLocal] = useState('');
  const messageRef = useRef(null);
  const location = useLocation();
  const scrolledKeyRef = useRef(null);
  const { refresh: refreshOnboarding } = useOnboarding();

  // Billing tab state
  const [billingSubscription, setBillingSubscription] = useState(null);
  const { credits, refreshCredits } = useCredits();
  const [billingLoading, setBillingLoading] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [topupLoading, setTopupLoading] = useState(null);
  const { topups, loading: plansLoading, error: plansError, reload: reloadPlans } = usePlans();

  useEffect(() => {
    fetchSettings();
  }, []);

  // /settings#<card>: open the Settings tab and scroll to that card once the page has rendered.
  // Cards above the target (delivery tracking, verified domains) load on their own and can push it
  // down, so it settles twice more unless the user has started scrolling themselves.
  useEffect(() => {
    const rawId = (location.hash || '').slice(1);
    let id = rawId;
    try {
      id = decodeURIComponent(rawId);
    } catch (e) {
      // malformed escape such as #%zz: use the raw text (it matches no card)
    }
    if (loading || !SETTINGS_ANCHORS.includes(id) || scrolledKeyRef.current === location.key) return undefined;
    if (activeTab !== 'settings') {
      setActiveTab('settings'); // the effect runs again once the tab has rendered
      return undefined;
    }
    scrolledKeyRef.current = location.key;
    const scrollToTarget = () => {
      // SendGrid's card only renders once a key is saved: fall back to the Email sending section
      const target = document.getElementById(id) || document.getElementById('email');
      // Browsers do not run a smooth scroll in a background tab (a link opened in a new tab)
      const behavior = document.visibilityState === 'visible' ? 'smooth' : 'auto';
      if (target) target.scrollIntoView({ behavior, block: 'start' });
    };
    let userScrolled = false;
    const onUserScroll = () => { userScrolled = true; };
    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    userEvents.forEach((e) => window.addEventListener(e, onUserScroll, { passive: true }));
    const timers = [0, 500, 1500].map((ms) => window.setTimeout(() => {
      if (!userScrolled) scrollToTarget();
    }, ms));
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      userEvents.forEach((e) => window.removeEventListener(e, onUserScroll));
    };
  }, [location.hash, location.key, loading, activeTab]);

  useEffect(() => {
    if (activeTab === 'billing') fetchBillingData();
  }, [activeTab]);

  // Auto-load verified Resend domains once an API key is saved, so the
  // sender-identity picker is populated without a manual step. Only fires
  // while the list is empty so editing the key field doesn't spam the API.
  useEffect(() => {
    if (settings.RESEND_API_KEY && resendDomains.length === 0) {
      fetchResendDomains({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.RESEND_API_KEY]);

  // onlyKeys: after a per-card save, refresh just that card's fields so unsaved edits elsewhere are kept
  const fetchSettings = async (onlyKeys = null) => {
    try {
      const data = await settingsAPI.getAll();
      if (data.success && onlyKeys) {
        const fresh = {};
        onlyKeys.forEach((k) => {
          fresh[k] = data.settings[k];
          fresh[`${k}_configured`] = data.settings[`${k}_configured`];
          fresh[`${k}_unreadable`] = data.settings[`${k}_unreadable`];
        });
        setSettings((s) => ({ ...s, ...fresh }));
        setSavedSettings((s) => ({ ...s, ...fresh }));
        const savedSender = data.settings.SENDER_EMAIL || '';
        if (onlyKeys.includes('SENDER_EMAIL') && savedSender.includes('@')) {
          const atIdx = savedSender.lastIndexOf('@');
          setSenderLocal(savedSender.slice(0, atIdx));
          setSenderDomain(savedSender.slice(atIdx + 1));
        }
      } else if (data.success) {
        setSettings(data.settings);
        setSavedSettings(data.settings);
        setSendGridConfigured(!!data.settings.SENDGRID_API_KEY);

        // Load domain configurations if any
        if (data.settings.domain_configs) {
          try {
            setDomainConfigs(JSON.parse(data.settings.domain_configs));
          } catch (e) {
            setDomainConfigs({});
          }
        }

        // Pre-fill the sender-identity picker from any saved SENDER_EMAIL
        const savedSender = data.settings.SENDER_EMAIL || '';
        if (savedSender.includes('@')) {
          const atIdx = savedSender.lastIndexOf('@');
          setSenderLocal(savedSender.slice(0, atIdx));
          setSenderDomain(savedSender.slice(atIdx + 1));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    setBillingLoading(true);
    try {
      refreshCredits();
      const [subRes] = await Promise.allSettled([
        api.get('/billing/subscription'),
      ]);
      setBillingSubscription(
        subRes.status === 'fulfilled' && subRes.value.data.has_subscription
          ? subRes.value.data.subscription
          : null
      );
    } catch (err) {
      console.error('Billing fetch error:', err);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api.post('/billing/reactivate');
      toast.success('Subscription reactivated!');
      fetchBillingData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!(await confirm({
      title: 'Cancel subscription',
      message: 'Cancel your subscription? You keep access until the end of your billing period.',
      confirmText: 'Cancel subscription',
      cancelText: 'Keep subscription'
    }))) return;
    setCancelling(true);
    try {
      await api.post('/billing/cancel', { immediate: false });
      toast.success('Cancellation scheduled — access continues until period end.');
      fetchBillingData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleTopUp = async (pkg) => {
    setTopupLoading(`topup-${pkg}`);
    try {
      const res = await api.post('/billing/tokens/topup', { package: pkg });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start checkout');
      setTopupLoading(null);
    }
  };

  const fetchResendDomains = async (opts = {}) => {
    const silent = opts.silent === true;
    if (!settings.RESEND_API_KEY) {
      return;
    }

    try {
      setLoadingDomains(true);
      const response = await api.get('/domains/from-resend');

      if (response.data.domains) {
        const verifiedDomains = response.data.domains.filter(
          d => d.verification_status === 'verified'
        );
        setResendDomains(verifiedDomains);

        if (!silent) {
          if (verifiedDomains.length === 0) {
            toast('No verified domains found in your Resend account.', {
              icon: 'ℹ️',
              duration: 5000
            });
          } else {
            toast.success(`Found ${verifiedDomains.length} verified domain(s)!`);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
      if (!silent) {
        toastSetupError(error.response?.data?.error, 'Failed to load domains from Resend');
      }
    } finally {
      setLoadingDomains(false);
    }
  };

  const isKeyChanged = (key) => normValue(key, settings[key]) !== normValue(key, savedSettings[key]);
  const isCardDirty = (card) => CARD_KEYS[card].some(isKeyChanged);

  // Shared save. card = null: "Save all changes" (every field, as before).
  // card = a CARD_KEYS id: only that card's changed fields, so masked secrets that were not edited are never re-sent.
  const saveSettings = async (card = null) => {
    const keys = card ? CARD_KEYS[card] : null;
    // The page-wide save scrolls to the message at the top; a card save shows its result on the card
    const scrollToMessage = () => {
      if (card) return;
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    };
    const fail = (text) => {
      setMessage({ type: 'error', text });
      if (card) setCardErrors((e) => ({ ...e, [card]: text }));
      scrollToMessage();
    };

    setMessage(null);
    if (card) {
      setCardErrors((e) => ({ ...e, [card]: null }));
      setCardSaved(null);
    }
    const copiesOn = settings.FORWARD_REPLIES_ENABLED !== 'false';
    if ((!keys || keys.includes('FORWARD_REPLIES_TO')) && copiesOn && !isValidReplyEmail(settings.FORWARD_REPLIES_TO)) {
      fail('Please enter a valid email address in "Send copies to" (or leave it empty).');
      return;
    }

    let settingsToSave;
    if (keys) {
      settingsToSave = {};
      keys.filter(isKeyChanged).forEach((k) => {
        settingsToSave[k] = settings[k] ?? '';
      });
    } else {
      settingsToSave = {
        ...settings,
        domain_configs: JSON.stringify(domainConfigs)
      };
    }
    if (typeof settingsToSave.FORWARD_REPLIES_TO === 'string') {
      settingsToSave.FORWARD_REPLIES_TO = settingsToSave.FORWARD_REPLIES_TO.trim();
    }
    // Copies are off and the (disabled) field is invalid: do not send it, keep the last saved value
    if (!copiesOn && 'FORWARD_REPLIES_TO' in settingsToSave && !isValidReplyEmail(settingsToSave.FORWARD_REPLIES_TO)) {
      delete settingsToSave.FORWARD_REPLIES_TO;
    }
    if (keys && Object.keys(settingsToSave).length === 0) return;

    if (card) setCardSaving(card);
    else setSaving(true);
    try {
      const response = await settingsAPI.update(settingsToSave);
      if (response.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        scrollToMessage();
        await fetchSettings(keys);
        refreshOnboarding();
        if (card) {
          setCardSaved(card);
          setTimeout(() => setCardSaved((c) => (c === card ? null : c)), 3000);
        }
      } else {
        fail(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      // Show the server's message when there is one; edits stay in state so nothing is lost
      fail(saveErrorText(error, null) || (error.response?.status >= 400 && error.response?.status < 500 && error.response?.data?.error) || 'Failed to save settings');
    } finally {
      if (card) setCardSaving(null);
      else setSaving(false);
    }
  };

  const handleSave = () => saveSettings();

  // Small Save button at the bottom-right of a card (render helper, not a component, so inputs keep focus)
  const renderCardSave = (card) => {
    const dirty = isCardDirty(card);
    const busy = cardSaving === card;
    return (
      <>
        {cardErrors[card] && <small className="card-save-error">{cardErrors[card]}</small>}
        {!dirty && !busy && cardSaved === card && (
          <span className="settings-pill-success card-save-done"><CheckCircle size={12} />Saved</span>
        )}
        <button
          type="button"
          onClick={() => saveSettings(card)}
          disabled={!dirty || busy || saving}
          className="card-save-button"
        >
          {busy ? (
            <>
              <RefreshCw size={14} className="spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={14} />
              Save
            </>
          )}
        </button>
      </>
    );
  };

  // Test buttons save the page first. Refresh the snapshot so per-card Save buttons go back to
  // "no changes", and the setup checklist so a key saved this way ticks its step.
  const saveBeforeTest = async () => {
    const autoSaved = await settingsAPI.update(settings);
    if (autoSaved?.success) {
      setSavedSettings(settings);
      refreshOnboarding();
    }
  };

  const testResendConnection = async () => {
    setTestingResend(true);
    setMessage(null);

    // Auto-save settings before testing
    try {
      await saveBeforeTest();
    } catch (error) {
      console.error('Error saving settings before test:', error);
      setMessage({ type: 'error', text: saveErrorText(error, 'Failed to save settings before testing') });
      setTestingResend(false);
      return;
    }

    try {
      const response = await settingsAPI.testResend();
      if (response.success && response.results) {
        const results = response.results;
        const diagnostics = results.diagnostic_results || [];
        const allSuccess = diagnostics.every(r => r.status === 'success');

        setMessage({
          type: allSuccess ? 'success' : 'error',
          text: allSuccess ? 'Resend connection successful!' : 'Resend connection issues detected',
          details: diagnostics.map(r => `${r.status.toUpperCase()}: ${r.message}`).join('\n'),
          troubleshooting: allSuccess ? null : diagnostics.filter(r => r.status !== 'success').map(r => r.message)
        });

        // Refresh the verified-domain list so the sender picker stays populated
        await fetchResendDomains({ silent: true });
      } else {
        setMessage({
          type: 'error',
          text: 'Resend connection failed',
          details: response.error || 'Unknown error',
          troubleshooting: response.troubleshooting
        });
      }
    } catch (error) {
      console.error('Error testing Resend:', error);
      setMessage({ type: 'error', text: `Failed to test connection: ${error.message}` });
    } finally {
      setTestingResend(false);
    }
  };

  const testSendGridConnection = async () => {
    setTestingSendGrid(true);
    setMessage(null);

    // Auto-save settings before testing
    try {
      await saveBeforeTest();
    } catch (error) {
      console.error('Error saving settings before test:', error);
      setMessage({ type: 'error', text: saveErrorText(error, 'Failed to save settings before testing') });
      setTestingSendGrid(false);
      return;
    }

    try {
      const response = await settingsAPI.testSendGrid();
      if (response.success) {
        setMessage({
          type: 'success',
          text: 'SendGrid connection successful!'
        });
      } else {
        setMessage({
          type: 'error',
          text: 'SendGrid connection failed',
          details: response.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Error testing SendGrid:', error);
      setMessage({ type: 'error', text: `Failed to test connection: ${error.message}` });
    } finally {
      setTestingSendGrid(false);
    }
  };

  const testSMTPConnection = async () => {
    setTestingSMTP(true);
    setMessage(null);

    // Auto-save settings before testing
    try {
      await saveBeforeTest();
    } catch (error) {
      console.error('Error saving settings before test:', error);
      setMessage({ type: 'error', text: saveErrorText(error, 'Failed to save settings before testing') });
      setTestingSMTP(false);
      return;
    }

    try {
      const response = await settingsAPI.testSMTP();
      if (response.success && response.results) {
        const results = response.results;
        const diagnostics = results.diagnostic_results || [];
        const allSuccess = diagnostics.every(r => r.status === 'success');

        setMessage({
          type: allSuccess ? 'success' : 'error',
          text: allSuccess ? 'SMTP connection successful!' : 'SMTP connection issues detected',
          details: diagnostics.map(r => `${r.status.toUpperCase()}: ${r.message}`).join('\n'),
          troubleshooting: allSuccess ? null : diagnostics.filter(r => r.status !== 'success').map(r => r.message)
        });
      } else {
        setMessage({
          type: 'error',
          text: 'SMTP connection failed',
          details: response.error || 'Unknown error',
          troubleshooting: response.troubleshooting
        });
      }
    } catch (error) {
      console.error('Error testing SMTP:', error);
      setMessage({ type: 'error', text: `Failed to test connection: ${error.message}` });
    } finally {
      setTestingSMTP(false);
    }
  };

  const testTwilioConnection = async () => {
    setTestingTwilio(true);
    setMessage(null);

    // Auto-save settings before testing
    try {
      await saveBeforeTest();
    } catch (error) {
      console.error('Error saving settings before test:', error);
      setMessage({ type: 'error', text: saveErrorText(error, 'Failed to save settings before testing') });
      setTestingTwilio(false);
      return;
    }

    try {
      const response = await settingsAPI.testTwilio();
      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Twilio connection successful!'
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Twilio connection failed',
          details: response.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Error testing Twilio:', error);
      setMessage({ type: 'error', text: `Failed to test connection: ${error.message}` });
    } finally {
      setTestingTwilio(false);
    }
  };

  const testAIConnection = async () => {
    setTestingAI(true);
    setMessage(null);

    // Auto-save settings before testing
    try {
      await saveBeforeTest();
    } catch (error) {
      console.error('Error saving settings before test:', error);
      setMessage({ type: 'error', text: saveErrorText(error, 'Failed to save settings before testing') });
      setTestingAI(false);
      return;
    }

    try {
      const response = await settingsAPI.testAI();
      if (response.success) {
        setMessage({
          type: 'success',
          text: 'AI connection successful!',
          details: response.model_tested ? `Model: ${response.model_tested}` : null
        });
      } else {
        setMessage({
          type: 'error',
          text: 'AI connection failed',
          details: response.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Error testing AI:', error);
      setMessage({ type: 'error', text: `Failed to test connection: ${error.response?.data?.error || error.message}` });
    } finally {
      setTestingAI(false);
    }
  };

  // Compose SENDER_EMAIL from the domain picker + local-part input
  const applySender = (local, domain) => {
    setSenderLocal(local);
    setSenderDomain(domain);
    const composed = local && domain ? `${local}@${domain}` : '';
    setSettings(s => ({ ...s, SENDER_EMAIL: composed }));
  };

  // Replies card: unset means app / enabled / empty
  const replyMode = settings.REPLY_MODE === 'mailbox' ? 'mailbox' : 'app';
  const forwardEnabled = settings.FORWARD_REPLIES_ENABLED !== 'false';
  const forwardEmailValid = isValidReplyEmail(settings.FORWARD_REPLIES_TO);

  const tabs = [
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'testing', label: 'Testing', icon: TestTube }
  ];

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      {confirmDialog}
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Configure your integrations and test your connections</p>
        <RouterLink to="/getting-started" className="settings-header-link">Setup checklist</RouterLink>
      </div>

      {/* Tab Navigation */}
      <div className="settings-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="settings-content">
        {activeTab === 'settings' && (
          <div className="settings-panel">
            {/* Section navigation (plain anchor links) */}
            <nav className="settings-nav" aria-label="Settings sections">
              <a href="#email">Email sending</a>
              <a href="#delivery-tracking">Delivery tracking</a>
              <a href="#replies">Replies</a>
              <a href="#sms">SMS</a>
              <a href="#ai">AI</a>
              <a href="#support">Help</a>
            </nav>

            {/* Message Display */}
            {message && (
              <div ref={messageRef} className={`message ${message.type}`}>
                <div className="message-icon">
                  {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                </div>
                <div className="message-content">
                  <strong>{message.text}</strong>
                  {message.details && (
                    <div className="message-details">{message.details}</div>
                  )}
                  {message.troubleshooting && message.troubleshooting.length > 0 && (
                    <div className="message-troubleshooting">
                      <strong>Troubleshooting Steps:</strong>
                      <ul>
                        {message.troubleshooting.map((tip, idx) => (
                          <li key={idx}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== Email sending ===== */}
            <SettingsSection
              id="email"
              title="Email sending"
              description="Connect the service your campaign emails are sent through, track their delivery and choose who they come from."
            >
              {/* Resend Settings */}
              <div id="resend" className="settings-card">
                <CardHead
                  icon={Mail}
                  title="Resend"
                  description="Email sending service with inbox support and automation capabilities"
                  configured={!!settings.RESEND_API_KEY_configured}
                />
                <SettingRow label="API Key" htmlFor="resend-api-key">
                  <input
                    id="resend-api-key"
                    type="password"
                    value={settings.RESEND_API_KEY || ''}
                    onChange={(e) => setSettings({ ...settings, RESEND_API_KEY: e.target.value })}
                    placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <UnreadableHint show={!!settings.RESEND_API_KEY_unreadable} />
                </SettingRow>
                {/* Sender identity: the address campaign emails send from (SENDER_EMAIL / SENDER_NAME), saved with the Resend card */}
                <div id="sender" className="sender-identity sender-identity--inline">
                  <h4 className="sender-identity-title">Sender identity</h4>
                  <p className="sender-identity-desc">The address and name your campaign emails are sent from. Set this before you test the connection.</p>
                {loadingDomains ? (
                  <p className="setting-loading">Loading verified domains from Resend…</p>
                ) : resendDomains.length > 0 ? (
                  <>
                    <SettingRow
                      label="From Domain"
                      htmlFor="sender-domain"
                      help="A domain you have verified in your Resend account. Emails can only be sent from a verified domain."
                    >
                      <select
                        id="sender-domain"
                        value={senderDomain}
                        onChange={(e) => applySender(senderLocal, e.target.value)}
                      >
                        <option value="">Select a verified domain</option>
                        {resendDomains.map(domain => (
                          <option key={domain.id} value={domain.domain}>{domain.domain}</option>
                        ))}
                      </select>
                    </SettingRow>
                    {senderDomain && (
                      <SettingRow
                        label="From Email Address"
                        htmlFor="sender-local"
                        help="The part before the @, for example hello or sales. This is the address recipients see."
                      >
                        <div className="setting-inline">
                          <input
                            id="sender-local"
                            type="text"
                            value={senderLocal}
                            onChange={(e) => applySender(e.target.value.replace(/\s+/g, ''), senderDomain)}
                            placeholder="e.g. noreply, hello, sales"
                          />
                          <span className="setting-suffix">@{senderDomain}</span>
                        </div>
                        <small className="setting-note">
                          Campaign emails will send from:{' '}
                          <strong>{senderLocal ? `${senderLocal}@${senderDomain}` : `(choose prefix)@${senderDomain}`}</strong>
                        </small>
                      </SettingRow>
                    )}
                    <SettingRow
                      label="Sender Name"
                      htmlFor="sender-name"
                      help={SENDER_NAME_HELP}
                    >
                      <input
                        id="sender-name"
                        type="text"
                        value={settings.SENDER_NAME || ''}
                        onChange={(e) => setSettings({ ...settings, SENDER_NAME: e.target.value })}
                        placeholder="Your Business Name"
                      />
                    </SettingRow>
                  </>
                ) : (
                  <>
                    <SettingRow
                      label="Sender Email"
                      htmlFor="sender-email"
                      help={(
                        <>
                          Save your Resend API key, then{' '}
                          <button
                            type="button"
                            onClick={() => fetchResendDomains()}
                            className="link-button"
                          >
                            load your verified domains
                          </button>{' '}
                          to pick from a list. Until then, type the full address — it must be on a domain verified in Resend.
                        </>
                      )}
                    >
                      <input
                        id="sender-email"
                        type="email"
                        value={settings.SENDER_EMAIL || ''}
                        onChange={(e) => setSettings({ ...settings, SENDER_EMAIL: e.target.value })}
                        placeholder="noreply@yourdomain.com"
                      />
                    </SettingRow>
                    <SettingRow label="Sender Name" htmlFor="sender-name" help={SENDER_NAME_HELP}>
                      <input
                        id="sender-name"
                        type="text"
                        value={settings.SENDER_NAME || ''}
                        onChange={(e) => setSettings({ ...settings, SENDER_NAME: e.target.value })}
                        placeholder="Your Business Name"
                      />
                    </SettingRow>
                  </>
                )}
                </div>
                {/* The shared events URL is deprecated (401 for customers): point to the personal address instead */}
                <WebhookSection title="Webhook Setup">
                  <p className="webhook-pointer">
                    Delivery events: use your personal webhook address in the{' '}
                    <a href="#delivery-tracking">Delivery tracking card</a> below.
                  </p>
                </WebhookSection>
                <div className="settings-card-foot">
                  <button
                    onClick={testResendConnection}
                    disabled={testingResend || !settings.RESEND_API_KEY}
                    className="test-button"
                  >
                    {testingResend ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Test Resend Connection
                      </>
                    )}
                  </button>
                  {renderCardSave('resend')}
                </div>
              </div>

              {/* Delivery tracking: Resend delivery events, kept next to the Resend sending card */}
              <div id="delivery-tracking" className="settings-anchor">
                <ResendWebhookCard />
              </div>

              {/* SendGrid Settings — shown only when a SendGrid key is already configured */}
              {sendGridConfigured && (
              <div id="sendgrid" className="settings-card">
                <CardHead
                  icon={Mail}
                  title="SendGrid"
                  description="Email sending with inbox support and automation capabilities"
                  configured={!!settings.SENDGRID_API_KEY_configured}
                />
                <SettingRow label="API Key" htmlFor="sendgrid-api-key">
                  <input
                    id="sendgrid-api-key"
                    type="password"
                    value={settings.SENDGRID_API_KEY || ''}
                    onChange={(e) => setSettings({ ...settings, SENDGRID_API_KEY: e.target.value })}
                    placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <UnreadableHint show={!!settings.SENDGRID_API_KEY_unreadable} />
                </SettingRow>
                <WebhookSection title="Webhook Setup">
                  <WebhookUrlField
                    label="Event Webhook URL"
                    url={`${BACKEND_URL}/api/webhooks/sendgrid/events`}
                    note="Add this under SendGrid Settings → Mail Settings → Event Webhook to track delivery, opens, and clicks."
                  />
                  <WebhookUrlField
                    label="Inbound Parse Webhook URL"
                    url={`${BACKEND_URL}/api/webhooks/sendgrid/inbound`}
                    note="Add this under SendGrid Settings → Inbound Parse if you want to receive replies in the inbox."
                  />
                </WebhookSection>
                <div className="settings-card-foot">
                  <button
                    onClick={testSendGridConnection}
                    disabled={testingSendGrid || !settings.SENDGRID_API_KEY}
                    className="test-button"
                  >
                    {testingSendGrid ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Test SendGrid Connection
                      </>
                    )}
                  </button>
                  {renderCardSave('sendgrid')}
                </div>
              </div>
              )} {/* end SendGrid hidden */}

              {/* SMTP Settings */}
              <div id="smtp" className="settings-card">
                <CardHead
                  icon={Server}
                  title="SMTP"
                  description="Custom SMTP server for email sending"
                  configured={!!settings.SMTP_PASSWORD_configured}
                />
                <div className="settings-card-intro">
                  <p>Most providers use port 587 (STARTTLS).</p>
                  <p className="settings-card-intro-note">Port 465 (implicit SSL) works too: we connect over SSL automatically when you use it.</p>
                </div>
                <SettingRow label="Server" htmlFor="smtp-server">
                  <input
                    id="smtp-server"
                    type="text"
                    value={settings.SMTP_SERVER || ''}
                    onChange={(e) => setSettings({ ...settings, SMTP_SERVER: e.target.value })}
                    placeholder="smtp.example.com"
                  />
                  <UnreadableHint show={!!settings.SMTP_SERVER_unreadable} />
                </SettingRow>
                <SettingRow label="Port" htmlFor="smtp-port">
                  <input
                    id="smtp-port"
                    type="number"
                    value={settings.SMTP_PORT || ''}
                    onChange={(e) => setSettings({ ...settings, SMTP_PORT: e.target.value })}
                    placeholder="587"
                  />
                  <UnreadableHint show={!!settings.SMTP_PORT_unreadable} />
                </SettingRow>
                <SettingRow label="Username" htmlFor="smtp-username">
                  <input
                    id="smtp-username"
                    type="text"
                    value={settings.SMTP_USERNAME || ''}
                    onChange={(e) => setSettings({ ...settings, SMTP_USERNAME: e.target.value })}
                    placeholder="user@example.com"
                  />
                  <UnreadableHint show={!!settings.SMTP_USERNAME_unreadable} />
                </SettingRow>
                <SettingRow label="Password" htmlFor="smtp-password">
                  <input
                    id="smtp-password"
                    type="password"
                    value={settings.SMTP_PASSWORD || ''}
                    onChange={(e) => setSettings({ ...settings, SMTP_PASSWORD: e.target.value })}
                    placeholder="••••••••"
                  />
                  <UnreadableHint show={!!settings.SMTP_PASSWORD_unreadable} />
                </SettingRow>
                <div className="settings-card-foot">
                  <button
                    onClick={testSMTPConnection}
                    disabled={testingSMTP || !settings.SMTP_SERVER}
                    className="test-button"
                  >
                    {testingSMTP ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Test SMTP Connection
                      </>
                    )}
                  </button>
                  {renderCardSave('smtp')}
                </div>
              </div>
            </SettingsSection>

            {/* ===== Replies ===== */}
            <SettingsSection
              id="replies"
              title="Replies"
              description="Decide where replies to your emails land."
            >
              {/* Replies: where replies to campaign emails go (REPLY_MODE / FORWARD_REPLIES_*) */}
              <div className="settings-card">
                <CardHead
                  icon={Inbox}
                  title="Replies"
                  description="Choose where replies to your emails should end up."
                />
                <SettingRow label="Where should replies go?">
                  <div className="choice-list" role="radiogroup" aria-label="Where should replies go?">
                    <label className="choice">
                      <input
                        type="radio"
                        name="reply-mode"
                        value="app"
                        checked={replyMode === 'app'}
                        onChange={() => setSettings({ ...settings, REPLY_MODE: 'app' })}
                      />
                      <span>
                        <span className="choice-title">Replies in Outreach Engine</span>
                        <small className="choice-help">
                          Your emails include a Reply-To address on our reply domain. When someone replies, the message
                          arrives in your Inbox here (and can also be copied to your own email below). Recipients will see
                          that Reply-To address when they hit Reply.
                        </small>
                      </span>
                    </label>
                    <label className="choice">
                      <input
                        type="radio"
                        name="reply-mode"
                        value="mailbox"
                        checked={replyMode === 'mailbox'}
                        onChange={() => setSettings({ ...settings, REPLY_MODE: 'mailbox' })}
                      />
                      <span>
                        <span className="choice-title">Replies to my own mailbox</span>
                        <small className="choice-help">
                          We do not add a Reply-To address. Replies go to your sender address in your own email app and
                          will not appear in your Inbox here.
                        </small>
                      </span>
                    </label>
                  </div>
                  <p className="choice-note">You can change this at any time; it only affects emails sent after you save.</p>
                </SettingRow>

                <SettingRow
                  label="Copy to your email"
                  help={'Only used with "Replies in Outreach Engine".'}
                >
                  <label className="choice">
                    <input
                      type="checkbox"
                      checked={forwardEnabled}
                      onChange={(e) => setSettings({ ...settings, FORWARD_REPLIES_ENABLED: e.target.checked ? 'true' : 'false' })}
                    />
                    <span>
                      <span className="choice-title">Also send me a copy of each reply by email</span>
                      <small className="choice-help">
                        The reply still lands in your Inbox here; the copy includes a link back to it.
                      </small>
                    </span>
                  </label>
                </SettingRow>

                <SettingRow label="Send copies to" htmlFor="forward-replies-to" help="The address that receives the copies.">
                  <input
                    id="forward-replies-to"
                    type="email"
                    value={settings.FORWARD_REPLIES_TO || ''}
                    onChange={(e) => setSettings({ ...settings, FORWARD_REPLIES_TO: e.target.value })}
                    placeholder="your account email"
                    disabled={!forwardEnabled}
                    aria-invalid={!forwardEmailValid}
                  />
                  {!forwardEmailValid ? (
                    <small className="setting-error">
                      Please enter a valid email address.
                    </small>
                  ) : (
                    <small className="setting-note">
                      Leave empty to use your account email.
                    </small>
                  )}
                </SettingRow>

                <small className="settings-card-footnote">
                  Only emails sent after you switch this on use the new reply address.
                </small>
                <div className="settings-card-foot settings-card-foot-plain">
                  {renderCardSave('replies')}
                </div>
              </div>
            </SettingsSection>

            {/* ===== SMS ===== */}
            <SettingsSection
              id="sms"
              title="SMS"
              description="Connect Twilio to send text messages and receive SMS replies."
            >
              {/* Twilio Settings */}
              <div id="twilio" className="settings-card">
                <CardHead
                  icon={MessageSquare}
                  title="Twilio"
                  description="SMS sending with inbox support and automation capabilities"
                  configured={!!(settings.TWILIO_SID_configured && settings.TWILIO_AUTH_configured)}
                />
                <SettingRow label="Account SID" htmlFor="twilio-sid">
                  <input
                    id="twilio-sid"
                    type="text"
                    value={settings.TWILIO_SID || ''}
                    onChange={(e) => setSettings({ ...settings, TWILIO_SID: e.target.value })}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <UnreadableHint show={!!settings.TWILIO_SID_unreadable} />
                </SettingRow>
                <SettingRow label="Auth Token" htmlFor="twilio-auth">
                  <input
                    id="twilio-auth"
                    type="password"
                    value={settings.TWILIO_AUTH || ''}
                    onChange={(e) => setSettings({ ...settings, TWILIO_AUTH: e.target.value })}
                    placeholder="••••••••••••••••••••••••••••••••"
                  />
                  <UnreadableHint show={!!settings.TWILIO_AUTH_unreadable} />
                </SettingRow>
                <SettingRow label="Phone Number" htmlFor="twilio-number">
                  <input
                    id="twilio-number"
                    type="tel"
                    value={settings.TWILIO_NUMBER || ''}
                    onChange={(e) => setSettings({ ...settings, TWILIO_NUMBER: e.target.value })}
                    placeholder="+1234567890"
                  />
                  <UnreadableHint show={!!settings.TWILIO_NUMBER_unreadable} />
                </SettingRow>
                <WebhookSection title="Webhook Setup">
                  <WebhookUrlField
                    label="SMS Status Webhook URL"
                    url={`${BACKEND_URL}/api/webhooks/twilio/status`}
                    note="Add this in your Twilio phone number settings under 'A MESSAGE STATUS CHANGES' to track delivery."
                  />
                  <WebhookUrlField
                    label="Inbound SMS Webhook URL"
                    url={`${BACKEND_URL}/api/webhooks/twilio/sms`}
                    note="Add this in your Twilio phone number settings under 'A MESSAGE COMES IN' to receive replies."
                  />
                </WebhookSection>
                <div className="settings-card-foot">
                  <button
                    onClick={testTwilioConnection}
                    disabled={testingTwilio || !settings.TWILIO_SID || !settings.TWILIO_AUTH}
                    className="test-button"
                  >
                    {testingTwilio ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Test Twilio Connection
                      </>
                    )}
                  </button>
                  {renderCardSave('twilio')}
                </div>
              </div>
            </SettingsSection>

            {/* ===== AI ===== */}
            <SettingsSection
              id="ai"
              title="AI"
              description="Add your OpenRouter key to generate templates and research prospects with AI."
            >
              {/* AI (OpenRouter) Settings */}
              <div id="ai-key" className="settings-card">
                <CardHead
                  icon={Brain}
                  title="AI (OpenRouter)"
                  description="One key for AI template writing and prospect research. You pay OpenRouter directly for what you use."
                  configured={!!settings.OPENROUTER_API_KEY_configured}
                />
                <SettingRow
                  label="OpenRouter API key"
                  htmlFor="openrouter-api-key"
                  help="Create a key at openrouter.ai/keys. You can set a spending limit on it there."
                >
                  <input
                    id="openrouter-api-key"
                    type="password"
                    value={settings.OPENROUTER_API_KEY || ''}
                    onChange={(e) => setSettings({ ...settings, OPENROUTER_API_KEY: e.target.value })}
                    placeholder="sk-or-v1-..."
                    autoComplete="off"
                  />
                  <UnreadableHint show={!!settings.OPENROUTER_API_KEY_unreadable} />
                </SettingRow>
                <SettingRow
                  label="Model"
                  htmlFor="ai-model"
                  help="The recommended model is a good balance of quality and cost. Each AI action is billed by OpenRouter to your own account."
                >
                  <select
                    id="ai-model"
                    value={modelSelectValue(settings.AI_MODEL, aiModelOther)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === OTHER_MODEL) {
                        setAiModelOther(true);
                      } else {
                        setAiModelOther(false);
                        setSettings({ ...settings, AI_MODEL: v });
                      }
                    }}
                  >
                    {AI_MODELS.map((m) => (
                      <option key={m.value || 'default'} value={m.value}>{m.label}</option>
                    ))}
                    <option value={OTHER_MODEL}>Other model...</option>
                  </select>
                  {modelSelectValue(settings.AI_MODEL, aiModelOther) === OTHER_MODEL && (
                    <input
                      id="ai-model-other"
                      type="text"
                      value={settings.AI_MODEL || ''}
                      onChange={(e) => setSettings({ ...settings, AI_MODEL: e.target.value })}
                      placeholder="provider/model-name, copied from openrouter.ai/models"
                      autoComplete="off"
                      aria-label="Other model id"
                    />
                  )}
                </SettingRow>
                {settings.OPENAI_API_KEY_configured && (
                  <small className="settings-card-footnote">
                    Your old OpenAI key is no longer used. AI features now use the OpenRouter key above.
                  </small>
                )}
                <div className="settings-card-foot">
                  <button
                    onClick={testAIConnection}
                    disabled={testingAI || !(settings.OPENROUTER_API_KEY || settings.OPENROUTER_API_KEY_configured)}
                    className="test-button"
                  >
                    {testingAI ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Test AI Connection
                      </>
                    )}
                  </button>
                  {renderCardSave('ai')}
                </div>
              </div>

              <ResearchProfilesCard />
            </SettingsSection>

            {/* ===== Help ===== */}
            <SettingsSection
              id="support"
              title="Help"
              description="Something not working as expected? Tell us about it."
            >
              {/* Report a Bug Section */}
              <div className="settings-card">
                <CardHead
                  icon={Bug}
                  title="Report a Bug"
                  description="Found an issue? Let us know so we can fix it quickly."
                />
                <div className="settings-card-foot settings-card-foot-plain">
                  <button
                    onClick={() => setBugReportOpen(true)}
                    className="test-button"
                  >
                    <Bug size={16} />
                    Report an Issue
                  </button>
                </div>
              </div>
            </SettingsSection>

            {bugReportOpen && (
              <BugReportForm onClose={() => setBugReportOpen(false)} />
            )}

            {/* Save Button (saves every field above) */}
            <div className="settings-actions">
              <span className="settings-actions-hint">Saves every card on this page at once.</span>
              <button
                onClick={handleSave}
                disabled={saving}
                className="save-button"
              >
                {saving ? (
                  <>
                    <RefreshCw size={18} className="spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save all changes
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="settings-panel">
            {billingLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="space-y-5">

                {/* Subscription status */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-base font-bold text-gray-900">Outreach Engine</h3>
                        {billingSubscription ? (
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                            billingSubscription.status === 'active' ? 'bg-green-100 text-green-700' :
                            billingSubscription.status === 'past_due' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {billingSubscription.status}
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-gray-100 text-gray-500">
                            No subscription
                          </span>
                        )}
                      </div>

                      {billingSubscription ? (
                        <>
                          <p className="text-sm text-gray-500 mb-0.5">
                            Billing: <span className="font-medium text-gray-700 capitalize">{billingSubscription.billing_cycle}</span>
                          </p>
                          <p className="text-sm text-gray-500">
                            {billingSubscription.cancel_at_period_end ? 'Access until:' : 'Renews:'}{' '}
                            <span className="font-medium text-gray-700">
                              {new Date(billingSubscription.current_period_end).toLocaleDateString()}
                            </span>
                          </p>
                          {billingSubscription.cancel_at_period_end && (
                            <div className="mt-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-yellow-800">Cancellation scheduled — you retain access until the end of your billing period.</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">No active subscription found.</p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 items-end flex-shrink-0">
                      {billingSubscription?.cancel_at_period_end && (
                        <button
                          onClick={handleReactivate}
                          disabled={reactivating}
                          className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          {reactivating ? 'Reactivating...' : 'Keep subscription'}
                        </button>
                      )}
                      {billingSubscription && !billingSubscription.cancel_at_period_end && (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancelling}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {cancelling ? 'Cancelling...' : 'Cancel subscription'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment & invoices */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="text-base font-bold text-gray-900 mb-1">Payment & Invoices</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Update your payment method or download past invoices via the ThriveCart customer portal.
                  </p>
                  <a
                    href="https://thrivecart.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Manage billing in ThriveCart
                  </a>
                </div>

                {/* Credits */}
                {(
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Zap className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-gray-900">Credits</h3>
                          <p className="text-xs text-gray-500">Used for searching and verifying prospect emails</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{credits ? credits.total.toLocaleString() : '—'}</p>
                        <p className="text-xs text-gray-500">remaining</p>
                      </div>
                    </div>

                    {credits && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {credits.monthly.toLocaleString()} monthly
                      </span>
                      {credits.topup > 0 && (
                        <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                          {credits.topup.toLocaleString()} top-up (never expire)
                        </span>
                      )}
                    </div>
                    )}

                    {credits && credits.total < 100 && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                        <p className="text-sm text-yellow-800">Running low — top up to keep searching and verifying prospects.</p>
                      </div>
                    )}

                    <p className="text-xs font-semibold text-gray-700 mb-3">Buy more credits (never expire):</p>
                    <TopUpPacks
                      topups={topups}
                      loading={plansLoading}
                      error={plansError}
                      onReload={reloadPlans}
                      onSelect={handleTopUp}
                      busyKey={topupLoading}
                    />
                  </div>
                )}

              </div>
            )}
          </div>
        )}

        {activeTab === 'testing' && (
          <TestPage />
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
